// src/lib/maps/GoogleMapContainer.js
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ensureGoogleMapsLoaded } from "./ensureGoogleMapsLoaded";

const getMapsKey = () => {
  const k = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "").trim();
  if (k) return k;

  if (typeof window !== "undefined" && window.__GOOGLE_MAPS_API_KEY) {
    return String(window.__GOOGLE_MAPS_API_KEY).trim();
  }

  return null;
};

const googleReady = () => typeof window !== "undefined" && !!window.google?.maps;
const safeNum = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);

const escHtml = (s) =>
  String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const domSafeId = (s) => String(s || "").replace(/[^a-zA-Z0-9_-]/g, "_");

const GoogleMapContainer = ({
  markers = [],
  polygons = [],
  circles = [],
  center,
  zoom,
  selectedMarkerId,
  onMarkerClick,
  onOpenAccount,
  onOpenSampleRequest,
  dataGeoJson = null,
  dataLayerStyle = null,
}) => {
  const hostRef = useRef(null);
  const mapRef = useRef(null);

  const markersRef = useRef(new Map());
  const polygonsRef = useRef(new Map());
  const circlesRef = useRef(new Map());
  const infoWindowRef = useRef(null);

  // ✅ Holds the latest marker data by id so click handlers never go stale
  const markerDataRef = useRef(new Map());

  // ✅ Holds latest callbacks so marker listeners never go stale
  const onMarkerClickRef = useRef(onMarkerClick);
  const onOpenAccountRef = useRef(onOpenAccount);
  const onOpenSampleRequestRef = useRef(onOpenSampleRequest);

  const [ready, setReady] = useState(false);
  const [mapsError, setMapsError] = useState("");

  useEffect(() => {
    onMarkerClickRef.current = onMarkerClick;
  }, [onMarkerClick]);

  useEffect(() => {
    onOpenAccountRef.current = onOpenAccount;
  }, [onOpenAccount]);

  useEffect(() => {
    onOpenSampleRequestRef.current = onOpenSampleRequest;
  }, [onOpenSampleRequest]);

  const makePinIcon = useCallback((g, color = "#ef4444", isSelected = false) => {
    const path =
      "M12 2C8.134 2 5 5.134 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.866-3.134-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z";

    return {
      path,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: "#ffffff",
      strokeOpacity: 1,
      strokeWeight: isSelected ? 2.5 : 2,
      scale: isSelected ? 1.55 : 1.35,
      anchor: new g.maps.Point(12, 24),
    };
  }, []);

  const openInfoWindowFor = useCallback(
    (g, map, markerInstance, markerId) => {
      try {
        const mk = markerDataRef.current.get(markerId) || {};

        const title = escHtml(mk?.title || "Account");
        const samples = Number(mk?.sampleCount || 0);
        const converted = Number(mk?.convertedCount || 0);
        const summaries = Array.isArray(mk?.sampleSummaries) ? mk.sampleSummaries : [];

        const listHtml = summaries.length
          ? `
          <div style="margin-top:10px;border-top:1px solid #eee;padding-top:8px;">
            <div style="font-weight:600;margin-bottom:6px;">Recent sample requests</div>
            <div style="display:flex;flex-direction:column;gap:6px;">
              ${summaries
                .map((s) => {
                  const rid = s?.id ? String(s.id) : "";
                  const btnId = `btn_req_${domSafeId(rid)}`;

                  const label = escHtml(s?.label || "Sample request");
                  const status = s?.status ? escHtml(s.status) : "—";

                  const isConv = s?.isConverted === true;
                  const convText = isConv ? "Converted" : "Not converted";
                  const convBg = isConv ? "#ecfdf5" : "#f3f4f6";
                  const convBorder = isConv ? "#a7f3d0" : "#e5e7eb";
                  const convColor = isConv ? "#047857" : "#374151";

                  let dateStr = "";
                  const sec = Number(s?.createdAtSeconds || 0);
                  if (sec > 0) {
                    const d = new Date(sec * 1000);
                    if (!Number.isNaN(d.getTime())) dateStr = d.toLocaleDateString();
                  }

                  return `
                    <button
                      id="${btnId}"
                      style="
                        text-align:left;
                        padding:6px 8px;
                        border:1px solid #e5e7eb;
                        border-radius:10px;
                        background:#fff;
                        cursor:pointer;
                        font-size:12px;
                        line-height:1.2;
                      "
                      title="Open this sample request"
                    >
                      <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:8px;">
                        <div style="min-width:0;">
                          <div style="font-weight:700;margin-bottom:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
                            ${label}
                          </div>
                          <div style="color:#6b7280;font-size:11px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
                            Status: <b style="color:#374151;">${status}</b>
                            ${dateStr ? ` &nbsp;•&nbsp; ${escHtml(dateStr)}` : ""}
                          </div>
                        </div>

                        <span
                          style="
                            flex:0 0 auto;
                            font-size:10px;
                            padding:3px 6px;
                            border-radius:999px;
                            border:1px solid ${convBorder};
                            background:${convBg};
                            color:${convColor};
                            white-space:nowrap;
                            font-weight:700;
                          "
                          title="Converted state"
                        >
                          ${convText}
                        </span>
                      </div>
                    </button>
                  `;
                })
                .join("")}
            </div>
          </div>
        `
          : `
          <div style="margin-top:10px;border-top:1px solid #eee;padding-top:8px;color:#6b7280;">
            No recent sample requests found for this account.
          </div>
        `;

        const safeMarkerId = domSafeId(markerId);

        const html = `
        <div style="font-size:12px;line-height:1.25;min-width:260px;max-width:340px;">
          <button
            id="btn_title_${safeMarkerId}"
            style="
              font-weight:800;
              margin-bottom:6px;
              padding:0;
              border:none;
              background:transparent;
              cursor:pointer;
              text-align:left;
              width:100%;
            "
            title="Open account"
          >${title}</button>

          <div style="color:#555;margin-bottom:10px;">
            Samples: <b>${samples}</b> &nbsp; Converted: <b>${converted}</b>
          </div>

          <div style="display:flex;gap:8px;flex-wrap:wrap;">
            <button
              id="btn_open_${safeMarkerId}"
              style="font-size:12px;padding:6px 10px;border:1px solid #ddd;border-radius:10px;background:#fff;cursor:pointer;font-weight:600;"
              title="Open this account"
            >
              Open Account
            </button>
          </div>

          ${listHtml}
        </div>
      `;

        infoWindowRef.current?.setContent(html);
        infoWindowRef.current?.open({ map, anchor: markerInstance });

        g.maps.event.addListenerOnce(infoWindowRef.current, "domready", () => {
          const btnTitle = document.getElementById(`btn_title_${safeMarkerId}`);
          if (btnTitle) {
            btnTitle.onclick = () =>
              onOpenAccountRef.current && onOpenAccountRef.current(markerId);
          }

          const btnOpen = document.getElementById(`btn_open_${safeMarkerId}`);
          if (btnOpen) {
            btnOpen.onclick = () =>
              onOpenAccountRef.current && onOpenAccountRef.current(markerId);
          }

          summaries.forEach((s) => {
            const rid = s?.id ? String(s.id) : "";
            if (!rid) return;
            const btnId = `btn_req_${domSafeId(rid)}`;
            const btn = document.getElementById(btnId);
            if (btn) {
              btn.onclick = () =>
                onOpenSampleRequestRef.current && onOpenSampleRequestRef.current(rid);
            }
          });
        });
      } catch (e) {
        console.warn("[GoogleMapContainer] Failed to open InfoWindow:", e);
      }
    },
    [markerDataRef, onOpenAccountRef, onOpenSampleRequestRef, infoWindowRef]
  );

  // ----------------------------
  // INIT MAP ONCE
  // ----------------------------
  useEffect(() => {
    let cancelled = false;

    // ✅ capture ref values to avoid cleanup warnings
    const markersMap = markersRef.current;
    const polygonsMap = polygonsRef.current;
    const circlesMap = circlesRef.current;

    const init = async () => {
      if (!hostRef.current) return;

      try {
        setMapsError("");

        const k = getMapsKey();
        console.log("[GoogleMapContainer] maps key?", !!k, k ? k.slice(0, 6) + "…" : "NONE");

        if (!googleReady()) {
          await ensureGoogleMapsLoaded({
            apiKey: k,
            libraries: [],
          });
        }

        if (cancelled || !hostRef.current || mapRef.current) return;

        const g = window.google;

        mapRef.current = new g.maps.Map(hostRef.current, {
          center: center || { lat: 39.5, lng: -98.35 },
          zoom: typeof zoom === "number" ? zoom : 4,
          mapTypeControl: false,
          fullscreenControl: false,
          streetViewControl: false,
        });

        infoWindowRef.current = new g.maps.InfoWindow();
        setReady(true);

        setTimeout(() => {
          try {
            g.maps.event.trigger(mapRef.current, "resize");
            if (center) mapRef.current.setCenter(center);
          } catch (e) {}
        }, 0);
      } catch (e) {
        console.error("[GoogleMapContainer] init error:", e);
        setMapsError(e?.message || "Failed to load Google Maps.");
        setReady(false);
      }
    };

    init();

    return () => {
      cancelled = true;

      const g = window.google;

      if (mapRef.current && g?.maps) {
        try {
          mapRef.current.data.forEach((f) => mapRef.current.data.remove(f));
        } catch (e) {}

        markersMap.forEach((m) => m.setMap(null));
        markersMap.clear();

        polygonsMap.forEach((p) => p.setMap(null));
        polygonsMap.clear();

        circlesMap.forEach((c) => c.setMap(null));
        circlesMap.clear();

        try {
          infoWindowRef.current?.close();
        } catch (e) {}
      }

      mapRef.current = null;
      infoWindowRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------
  // CENTER / ZOOM UPDATES
  // ----------------------------
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const m = mapRef.current;

    try {
      if (center && safeNum(center.lat) !== null && safeNum(center.lng) !== null) {
        m.setCenter(center);
      }
      if (typeof zoom === "number" && Number.isFinite(zoom)) {
        m.setZoom(zoom);
      }
    } catch (e) {
      console.warn("[GoogleMapContainer] center/zoom update failed:", e);
    }
  }, [ready, center, zoom]);

  // ----------------------------
  // GEOJSON DATA LAYER (optional)
  // ----------------------------
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const m = mapRef.current;

    try {
      m.data.forEach((f) => m.data.remove(f));
    } catch (e) {}

    if (!dataGeoJson) return;

    try {
      m.data.addGeoJson(dataGeoJson);
    } catch (e) {
      console.error("[GoogleMapContainer] addGeoJson failed:", e);
    }
  }, [ready, dataGeoJson]);

  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const m = mapRef.current;

    try {
      if (typeof dataLayerStyle === "function") {
        m.data.setStyle(dataLayerStyle);
      } else {
        m.data.setStyle({ visible: false });
      }
    } catch (e) {
      console.warn("[GoogleMapContainer] data layer style failed:", e);
    }
  }, [ready, dataLayerStyle]);

  // ----------------------------
  // ✅ Keep latest marker data in a ref (prevents stale InfoWindow)
  // ----------------------------
  useEffect(() => {
    const next = new Map();
    (Array.isArray(markers) ? markers : []).forEach((mk) => {
      if (mk?.id) next.set(mk.id, mk);
    });
    markerDataRef.current = next;
  }, [markers]);

  // ----------------------------
  // MARKERS SYNC
  // ----------------------------
  useEffect(() => {
    if (!ready || !mapRef.current) return;

    const g = window.google;
    const m = mapRef.current;

    const next = Array.isArray(markers) ? markers : [];
    const keepIds = new Set();

    next.forEach((mk) => {
      const id = mk?.id;
      const lat = safeNum(mk?.lat);
      const lng = safeNum(mk?.lng);
      if (!id || lat === null || lng === null) return;

      keepIds.add(id);

      const isSelected = selectedMarkerId && id === selectedMarkerId;
      const pos = { lat, lng };

      let markerInstance = markersRef.current.get(id);

      if (!markerInstance) {
        markerInstance = new g.maps.Marker({
          map: m,
          position: pos,
          title: mk?.title || "",
          icon: makePinIcon(g, mk?.pinColor || "#ef4444", isSelected),
          zIndex: isSelected ? 9999 : 1,
        });

        markerInstance.addListener("click", () => {
          onMarkerClickRef.current && onMarkerClickRef.current({ id });
          openInfoWindowFor(g, m, markerInstance, id);
        });

        markersRef.current.set(id, markerInstance);
      } else {
        markerInstance.setPosition(pos);
        markerInstance.setTitle(mk?.title || "");
      }

      try {
        markerInstance.setIcon(makePinIcon(g, mk?.pinColor || "#ef4444", isSelected));
        markerInstance.setZIndex(isSelected ? 9999 : 1);
      } catch (e) {}
    });

    Array.from(markersRef.current.keys()).forEach((id) => {
      if (!keepIds.has(id)) {
        const markerInstance = markersRef.current.get(id);
        try {
          markerInstance?.setMap(null);
        } catch (e) {}
        markersRef.current.delete(id);
      }
    });
  }, [ready, markers, selectedMarkerId, makePinIcon, openInfoWindowFor]);

  // ----------------------------
  // POLYGONS SYNC
  // ----------------------------
  useEffect(() => {
    if (!ready || !mapRef.current) return;

    const g = window.google;
    const m = mapRef.current;

    const next = Array.isArray(polygons) ? polygons : [];
    const keepIds = new Set();

    next.forEach((p) => {
      const id = p?.id;
      const paths = Array.isArray(p?.paths) ? p.paths : null;
      if (!id || !paths || paths.length < 3) return;

      keepIds.add(id);

      let inst = polygonsRef.current.get(id);

      const opts = {
        map: m,
        paths,
        clickable: false,
        strokeColor: p.strokeColor || "#7c3aed",
        strokeOpacity: typeof p.strokeOpacity === "number" ? p.strokeOpacity : 0.9,
        strokeWeight: typeof p.strokeWeight === "number" ? p.strokeWeight : 2,
        fillColor: p.fillColor || "#a855f7",
        fillOpacity: typeof p.fillOpacity === "number" ? p.fillOpacity : 0.15,
        zIndex: typeof p.zIndex === "number" ? p.zIndex : 2,
      };

      if (!inst) {
        inst = new g.maps.Polygon(opts);
        polygonsRef.current.set(id, inst);
      } else {
        inst.setOptions(opts);
      }
    });

    Array.from(polygonsRef.current.keys()).forEach((id) => {
      if (!keepIds.has(id)) {
        const inst = polygonsRef.current.get(id);
        try {
          inst?.setMap(null);
        } catch (e) {}
        polygonsRef.current.delete(id);
      }
    });
  }, [ready, polygons]);

  // ----------------------------
  // CIRCLES SYNC
  // ----------------------------
  useEffect(() => {
    if (!ready || !mapRef.current) return;

    const g = window.google;
    const m = mapRef.current;

    const next = Array.isArray(circles) ? circles : [];
    const keepIds = new Set();

    next.forEach((c) => {
      const id = c?.id;
      const lat = safeNum(c?.center?.lat);
      const lng = safeNum(c?.center?.lng);
      const radiusMeters = safeNum(c?.radiusMeters);

      if (!id || lat === null || lng === null || radiusMeters === null) return;

      keepIds.add(id);

      let inst = circlesRef.current.get(id);

      const opts = {
        map: m,
        center: { lat, lng },
        radius: radiusMeters,
        strokeColor: c.strokeColor || "#2563eb",
        strokeOpacity: typeof c.strokeOpacity === "number" ? c.strokeOpacity : 0.8,
        strokeWeight: typeof c.strokeWeight === "number" ? c.strokeWeight : 2,
        fillColor: c.fillColor || "#60a5fa",
        fillOpacity: typeof c.fillOpacity === "number" ? c.fillOpacity : 0.15,
        clickable: false,
        zIndex: typeof c.zIndex === "number" ? c.zIndex : 1,
      };

      if (!inst) {
        inst = new g.maps.Circle(opts);
        circlesRef.current.set(id, inst);
      } else {
        inst.setOptions(opts);
      }
    });

    Array.from(circlesRef.current.keys()).forEach((id) => {
      if (!keepIds.has(id)) {
        const inst = circlesRef.current.get(id);
        try {
          inst?.setMap(null);
        } catch (e) {}
        circlesRef.current.delete(id);
      }
    });
  }, [ready, circles]);

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      <div ref={hostRef} style={{ width: "100%", height: "100%" }} />
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center bg-white text-xs text-gray-600">
          {mapsError ? (
            <div className="max-w-[420px] px-4 text-center text-red-600">
              {mapsError}
              <div className="mt-2 text-gray-500">
                Check console for “Google Maps JavaScript API error …” (InvalidKeyMapError,
                RefererNotAllowedMapError, BillingNotEnabledMapError).
              </div>
            </div>
          ) : (
            "Loading Google Maps…"
          )}
        </div>
      )}
    </div>
  );
};

export default GoogleMapContainer;
